import { useState } from "react";

import { Box, colors, Divider, Grid, Icon, Modal } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";

import { formatDateEndOfDay } from "helpers/formatDate";

import { editInscricaoAtleta } from "services/inscricoes";
import {
  CategoriaAtletaInscrito,
  IInscricaoEvento,
  IListInscrito,
} from "interfaces/inscricoes";
import { setMessage, useMaterialUIController } from "context";

interface Props {
  open: boolean;
  handleOpen: () => void;
  update: (inscUpdate: any) => void;
  inscricao: IListInscrito;
  inscricaoId: number;
  evento: IInscricaoEvento;
}

export default function ModalEditInscricao({
  open,
  handleOpen,
  update,
  inscricao,
  inscricaoId,
  evento,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [messageError, setMessageError] = useState<string | null>(null);
  const [camisetaSelected, setCamisetaSelected] = useState(
    inscricao.tamanhoCamiseta || null
  );
  const [distanciaSelected, setDistanciaSelected] =
    useState<CategoriaAtletaInscrito | null>(inscricao.categoria || null);

  const yearCurrent = new Date().getFullYear();
  const idadeAtleta =
    yearCurrent - new Date(inscricao.atleta.dataNascimento).getFullYear();
  const camisetas = evento.camisetas || [];
  const distancias = evento.distanciaEvento || [];

  // selecionar camiseta
  const handleSelectCamiseta = (camiseta: string) =>
    setCamisetaSelected(camiseta);

  // Selecionar distancia
  const handleSelectDistancia = (distancia: any) => {
    if (inscricao.atleta) {
      const sexoAtleta = inscricao.atleta.sexo.tipo;

      // Verificar em qual categoria o atleta se enquadra
      const categoriaAtleta = distancia.categoriaEvento
        .filter(
          (item: any) =>
            item.sexo.tipo === sexoAtleta || item.sexo.tipo === "Masc/Fem"
        )
        .find(
          (idade: any) =>
            idadeAtleta >= idade.idadeInicial && idadeAtleta <= idade.idadefinal
        );

      if (categoriaAtleta) {
        setMessageError(null);
        setDistanciaSelected({
          distancia: { ...distancia },
          ...categoriaAtleta,
        });
      } else {
        setMessageError(
          `Atleta não se enquadra na categoria '${distancia.descricao}'`
        );
      }
    }
  };

  // Salvar alterações
  const handleSave = async () => {
    // Salvar os dados do atleta
    const { data, status } = await editInscricaoAtleta({
      atleta_id: inscricao.atleta.id,
      categoriaId: distanciaSelected.id,
      eventoId: evento.id,
      tamCam: camisetaSelected || "",
      inscricaoId: inscricaoId,
    });

    if (status === 200) {
      setMessage(dispatch, {
        open: true,
        text: "Inscrição alterada com sucesso",
      });
      update(data);
      handleOpen();
    } else {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Ops! Algo deu errado. Por favor, tente novamente.",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleOpen}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        sx={{
          ...style,
          width: { xs: "95%", sm: 500, md: 940 },
          p: 3,
          position: "relative",
        }}
      >
        <MDBox>
          <MDTypography variant="h5">Editar Inscrição</MDTypography>
          <MDTypography variant="subtitle2" color="text">
            {evento.titulo}
          </MDTypography>
        </MDBox>

        <Divider />

        {/* DADOS DO ATLETA */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <MDBox>
              <MDTypography variant="body2" color="info" fontWeight="bold">
                {inscricao?.atleta?.nome}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                CPF: {inscricao?.atleta?.cpf}
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} sm={4}>
            <MDBox>
              <MDTypography variant="body2" color="text">
                Dt. Nasc.:{" "}
                {formatDateEndOfDay(inscricao?.atleta?.dataNascimento)}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Sexo: {inscricao?.atleta?.sexo.tipo}
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid
            item
            xs={12}
            sm={2}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={1}
              borderRadius="5px"
              width="60px"
              sx={{ backgroundColor: "#000000" }}
            >
              <MDTypography variant="body1" color="white">
                {idadeAtleta}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                anos
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={7}>
            {/* Distancia & Categorias */}
            <MDBox mt={2}>
              <MDTypography variant="subtitle2" color="text" fontWeight="bold">
                Distâncias & Categorias
              </MDTypography>

              <MDBox
                display="flex"
                flexDirection="row"
                gap={1}
                sx={{ overflowX: "auto" }}
              >
                {distancias.map((distancia) => (
                  <MDBox
                    key={distancia.id}
                    component="button"
                    onClick={() => handleSelectDistancia(distancia)}
                    mt={1}
                    sx={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "120px",
                      width: "140px",
                      cursor: "pointer",
                      borderRadius: "5px",
                      backgroundColor:
                        distanciaSelected.distancia.descricao ===
                        distancia.descricao
                          ? colors.grey[100]
                          : "",
                      border: `1px solid ${
                        distanciaSelected.distancia.descricao ===
                        distancia.descricao
                          ? colors.blue[800]
                          : colors.grey[400]
                      }`,
                      ":hover": {
                        backgroundColor: colors.grey[50],
                        border: `1px solid ${colors.blue[800]}`,
                      },
                    }}
                  >
                    <MDBox sx={{ position: "absolute", top: 10, right: 10 }}>
                      <Icon
                        fontSize="medium"
                        color={
                          distanciaSelected.distancia.descricao ===
                          distancia.descricao
                            ? "success"
                            : "secondary"
                        }
                      >
                        {distanciaSelected.distancia.descricao ===
                        distancia.descricao
                          ? "check_circle_outline"
                          : "radio_button_unchecked"}
                      </Icon>
                    </MDBox>

                    <MDTypography
                      variant="body2"
                      color="text"
                      fontWeight="bold"
                    >
                      {distancia.descricao}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
              {messageError && (
                <MDAlert color="info" dismissible sx={{ mt: 2 }}>
                  {messageError}
                </MDAlert>
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={5}>
            {/* Camiseta */}
            <MDBox mt={2}>
              <MDTypography variant="subtitle2" color="text" fontWeight="bold">
                Tamanho Camiseta
              </MDTypography>
              <MDBox
                display="flex"
                flexDirection="row"
                gap={1}
                sx={{ overflowX: "auto" }}
              >
                {camisetas.length === 0 && (
                  <MDTypography variant="body2" color="text">
                    Nenhuma camiseta disponível para o evento
                  </MDTypography>
                )}
                {camisetas.map((camiseta) => (
                  <MDBox
                    key={camiseta.tamanho}
                    component="button"
                    onClick={() => handleSelectCamiseta(camiseta.tamanho)}
                    mt={1}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "60px",
                      width: "60px",
                      cursor: "pointer",
                      borderRadius: "5px",
                      backgroundColor:
                        camisetaSelected === camiseta.tamanho
                          ? colors.grey[100]
                          : "",
                      border: `1px solid ${
                        camisetaSelected === camiseta.tamanho
                          ? colors.blue[800]
                          : colors.grey[400]
                      }`,
                      ":hover": {
                        backgroundColor: colors.grey[50],
                        border: `1px solid ${colors.blue[800]}`,
                      },
                    }}
                  >
                    <MDTypography
                      variant="body2"
                      color="text"
                      fontWeight="bold"
                    >
                      {camiseta.tamanho}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>

        <Divider />

        {/* AÇÕES */}
        <MDBox
          display="flex"
          flexDirection="row"
          gap={1}
          justifyContent="flex-end"
        >
          <MDButton variant="gradient" color="info" onClick={handleSave}>
            Salvar alterações
          </MDButton>
          <MDButton variant="gradient" color="light" onClick={handleOpen}>
            Cancelar
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
}

const style = {
  position: "absolute" as "absolute",
  borderRadius: "5px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};
